import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useLanguage } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import { type I18nContent, KnockFeedProvider, KnockGuideProvider, KnockProvider } from '@knocklabs/react';
import '@knocklabs/react/dist/index.css';
import type { ReactNode } from 'react';

/**
 * Tradução pt-BR do chrome dos componentes de feed/guide do Knock (`@knocklabs/react`).
 *
 * O Knock só publica os locales `en` e `de`, então o pt-BR precisa ser fornecido pelo client
 * via prop `i18n` do `KnockProvider`. É `Partial<Translations>` — só as chaves que o feed
 * in-app usa; o resto cai no default em inglês do Knock. Não confundir com as traduções
 * server-side (CLI `translations/<locale>/...json`), que traduzem o conteúdo dos templates.
 */
const KNOCK_PT_BR_I18N: I18nContent = {
  locale: 'pt-BR',
  translations: {
    notifications: 'Notificações',
    markAllAsRead: 'Marcar todas como lidas',
    archiveNotification: 'Arquivar esta notificação',
    archiveRead: 'Arquivar lidas',
    emptyFeedTitle: 'Nenhuma notificação ainda',
    emptyFeedBody: 'Avisaremos quando tivermos algo novo para você.',
    all: 'Todas',
    unread: 'Não lidas',
    read: 'Lidas',
    unseen: 'Não vistas',
  },
};

/**
 * Configura os providers de UI in-app do Knock para usuários autenticados.
 *
 * Montado uma vez no topo da árvore privada (acima do layout switcher) para que o sino do
 * feed in-app do header (`KnockFeedButton`) e o modal global de guide (`KnockGuideModal`)
 * compartilhem um único client + websocket do Knock durante toda a sessão.
 *
 * Protegido pela feature flag `knock-in-app` (`knockInApp` no client). A config vem de `@cobliteam/react-environments`
 * (`knockAPIKey`, `knockFeedChannelId`, `knockGuideChannelId`), que leem as variáveis de
 * ambiente `REACT_APP_KNOCK_*` com defaults de prod. Cada provider aninhado é condicionado ao
 * seu próprio channel id, e as superfícies de feed/modal são condicionadas à mesma flag +
 * valores, então uma superfície nunca chama um hook do Knock sem o seu provider.
 *
 * O locale do Knock acompanha o idioma do app (`useLanguage`): pt-BR usa `KNOCK_PT_BR_I18N`,
 * os demais caem no default `en` do Knock.
 */
const KnockInAppProvider = ({ children }: { children: ReactNode }) => {
  const { getSession } = useUser();
  const session = getSession();
  const userId = session?.user?.id;
  const fleetId = session?.fleet?.id;
  const { knockInApp } = useFeatureFlags();
  const { currentLanguage } = useLanguage();

  const { knockAPIKey, knockFeedChannelId, knockGuideChannelId } = Environment.getInstance().getEnvironment();

  // Session+user são garantidos pelo PrivateRouterProvider, mas aborta se o Knock estiver off/não configurado.
  if (!(knockInApp && knockAPIKey && userId)) return <>{children}</>;

  const knockI18n = currentLanguage?.toLowerCase().startsWith('pt') ? KNOCK_PT_BR_I18N : undefined;

  let tree = <>{children}</>;

  if (knockGuideChannelId) {
    tree = (
      <KnockGuideProvider channelId={knockGuideChannelId} readyToTarget targetParams={{ tenant: fleetId }}>
        {tree}
      </KnockGuideProvider>
    );
  }

  if (knockFeedChannelId) {
    tree = (
      // `mode: 'rich'` mantém arrays/objetos aninhados em `data` (risk_factors, suggestions, metadata);
      // o default `compact` os remove e o feed do header divergiria do card flutuante de risco.
      <KnockFeedProvider feedId={knockFeedChannelId} defaultFeedOptions={{ tenant: fleetId, mode: 'rich' }}>
        {tree}
      </KnockFeedProvider>
    );
  }

  return (
    <KnockProvider apiKey={knockAPIKey} user={{ id: String(userId) }} i18n={knockI18n}>
      {tree}
    </KnockProvider>
  );
};

export default KnockInAppProvider;
